import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Languages, Loader2, Trash2 } from "lucide-react";
import { API } from "../../App";
import { useLanguage } from "../../i18n/LanguageContext";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, withCredentials: true });

// Admin opt-in translation of one page: pick a language + engine, click Translate. Nothing runs automatically.
export const TranslateControl = ({ layoutKey, onDone }) => {
  const { langs, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("es");
  const [engine, setEngine] = useState("deepl");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [deepl, setDeepl] = useState(false);

  const refresh = () => axios.get(`${API}/admin/translate/status`, { params: { key: layoutKey }, ...auth() }).then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { if (open) { refresh(); axios.get(`${API}/translate/languages`).then((r) => setDeepl(!!r.data.deepl_connected)).catch(() => {}); } }, [open, layoutKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    const l = langs.find((x) => x.code === target);
    if (!window.confirm(`Translate this page into ${l?.name || target} with ${engine === "deepl" ? "DeepL" : "Claude (AI)"}? About ${status?.strings ?? "?"} text pieces will be sent to that service. Visitors only see it after they pick the language.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/translate/layout`, { key: layoutKey, lang: target, engine }, auth());
      toast.success(`${l?.native || target} ready — switch the language picker to review it`);
      refresh(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Translation failed"); } finally { setBusy(false); }
  };
  const remove = async (lang) => {
    if (!window.confirm("Remove this translation? Visitors in that language will see English again.")) return;
    await axios.delete(`${API}/admin/translate/layout`, { params: { key: layoutKey, lang }, ...auth() }); refresh(); onDone?.();
  };

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className="h-8 inline-flex items-center gap-1 rounded-full border border-white/40 bg-white/10 px-3 text-xs font-bold hover:bg-white/20" data-testid="translate-control-btn"><Languages className="h-3.5 w-3.5" /> Translate</button>
      {open && (
        <div className="absolute right-0 mt-1 z-[130] w-80 rounded-xl bg-white text-[#1A1A1A] shadow-2xl ring-1 ring-black/10 p-3 space-y-2 text-xs" data-testid="translate-control-panel">
          <p className="font-bold">Translate this page</p>
          <p className="text-[#5C5C5C]">Opt-in only — nothing is translated until you click. {status ? `${status.strings} text pieces on this page.` : ""}</p>
          <div className="flex gap-2">
            <select value={target} onChange={(e) => setTarget(e.target.value)} className="flex-1 rounded-md border border-[#E2E0DC] px-2 py-1.5" data-testid="translate-lang">
              {langs.map((l) => <option key={l.code} value={l.code}>{l.native} · {l.name}{l.deepl ? "" : " (Claude only)"}</option>)}
            </select>
            <select value={engine} onChange={(e) => setEngine(e.target.value)} className="rounded-md border border-[#E2E0DC] px-2 py-1.5" data-testid="translate-engine">
              <option value="deepl">DeepL{deepl ? "" : " (not connected)"}</option>
              <option value="claude">Claude (AI)</option>
            </select>
          </div>
          <button type="button" onClick={run} disabled={busy} className="w-full rounded-full bg-[#2E5C55] text-white py-2 font-bold disabled:opacity-60 inline-flex items-center justify-center gap-1.5" data-testid="translate-run">
            {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Translating…</> : "Translate now"}
          </button>
          {status?.translations?.length > 0 && (
            <ul className="divide-y divide-[#EEECE8] rounded-md border border-[#E2E0DC]" data-testid="translate-existing">
              {status.translations.map((t) => (
                <li key={t.lang} className="flex items-center gap-2 px-2 py-1.5">
                  <button type="button" onClick={() => { setLang(t.lang); setOpen(false); }} className="font-bold underline" data-testid={`translate-view-${t.lang}`}>{langs.find((l) => l.code === t.lang)?.native || t.lang}</button>
                  <span className="text-[#5C5C5C]">{t.engine === "deepl" ? "DeepL" : "Claude"} · {t.status}{t.stale ? " · English changed since" : ""}</span>
                  <button type="button" onClick={async () => { await axios.patch(`${API}/admin/translate/layout/status`, null, { params: { key: layoutKey, lang: t.lang, status: t.status === "reviewed" ? "machine" : "reviewed" }, ...auth() }); refresh(); onDone?.(); }} className="text-[#2E5C55] underline" data-testid={`translate-review-${t.lang}`}>{t.status === "reviewed" ? "unmark" : "mark reviewed"}</button>
                  <button type="button" onClick={() => remove(t.lang)} className="ml-auto text-red-500" aria-label="Remove translation" data-testid={`translate-remove-${t.lang}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
